import React, { useState } from 'react'
import { useLocation, Link } from 'react-router-dom'
import { Box, List, ListItem, Typography } from '@mui/material'
import { PiCaretDownBold, PiCaretUpBold } from 'react-icons/pi'
import {
  MdOutlineDashboardCustomize,
  MdOutlineMessage,
  MdOutlineNotificationsNone,
  MdOutlineReportProblem,
  MdOutlineSettings,
} from 'react-icons/md'
import { LuUsers } from 'react-icons/lu'
import {
  BsHouseDoor,
  BsGraphUpArrow,
  BsStars,
  BsQuestionCircle,
} from 'react-icons/bs'
import { LiaHandshake } from 'react-icons/lia'
import { FaRegNewspaper } from 'react-icons/fa'
import { TbMessageReport } from 'react-icons/tb'

const menuItems = [
  {
    path: '/dashboard',
    label: 'Overview',
    icon: <MdOutlineDashboardCustomize />,
  },
  {
    path: '/users',
    label: 'User Management',
    icon: <LuUsers />,
    children: [
      { path: '/users/agents', label: 'Agents' },
      { path: '/users/customers', label: 'Customers' },
    ],
  },
  {
    path: '/properties',
    label: 'Property Management',
    icon: <BsHouseDoor />,
  },
  { path: '/analytics', label: 'Analytics', icon: <BsGraphUpArrow /> },
  { path: '/activity', label: 'Activity', icon: <LiaHandshake /> },
  // { path: '/subscription', label: 'Subscription', icon: <FaRegNewspaper /> },
  { path: '/reports', label: 'Reports', icon: <MdOutlineReportProblem /> },
  {
    path: '/reviews',
    label: 'Reviews',
    icon: <BsStars />,
    children: [
      { path: '/reviews/general', label: 'General Review' },
      { path: '/reviews/survey', label: 'Survey Review' },
    ],
  },
  { path: '/messages', label: 'Message', icon: <MdOutlineMessage /> },
  {
    path: '/notifications',
    label: 'Notification',
    icon: <MdOutlineNotificationsNone />,
  },
  { path: '/settings', label: 'Settings', icon: <MdOutlineSettings /> },
]

const footerItems = [
  { path: '/feedback', label: 'Feedback', icon: <TbMessageReport /> },
  {
    path: '/help',
    label: 'Help',
    icon: <BsQuestionCircle />,
    children: [
      { path: '/help/faq', label: 'FAQ' },
      { path: '/help/contact', label: 'Contact Us' },
    ],
  },
]

const Sidebar = () => {
  const [openMenus, setOpenMenus] = useState({})
  const location = useLocation()

  React.useEffect(() => {
    const newOpenMenus = {}
    menuItems.concat(footerItems).forEach(item => {
      if (item.children) {
        const hasActiveChild = item.children.some(
          child =>
            location.pathname === child.path ||
            location.pathname.startsWith(child.path + '/'),
        )
        if (hasActiveChild) {
          newOpenMenus[item.path] = true
        }
      }
    })
    setOpenMenus(newOpenMenus)
  }, [location.pathname])

  const isActive = (path, children = []) => {
    if (location.pathname === path) {
      return true
    }
    if (location.pathname.startsWith(path + '/')) {
      return true
    }
    if (children.length > 0) {
      return children.some(
        child =>
          location.pathname === child.path ||
          location.pathname.startsWith(child.path + '/'),
      )
    }
    return false
  }

  const toggleMenu = path => {
    setOpenMenus(prev => ({
      ...prev,
      [path]: !prev[path],
    }))
  }

  const renderMenuItem = item => {
    const active = isActive(item.path, item.children || [])
    const isOpen = openMenus[item.path] || false

    const baseClasses =
      'flex items-center gap-3 py-2 px-4 rounded-md text-sm font-normal transition-colors duration-200 ease-in-out whitespace-nowrap'
    const activeClasses =
      'bg-primary text-white font-bold pointer-events-none cursor-default'
    const inactiveClasses =
      'text-gray-600 hover:bg-gray-100 focus:bg-gray-100 hover:text-black focus:text-black'

    const itemContent = (
      <>
        {item.icon && <span className="text-xl">{item.icon}</span>}
        <span className="whitespace-nowrap">{item.label}</span>
        {item.children && (
          <span className="ml-auto text-sm">
            {isOpen ? <PiCaretUpBold /> : <PiCaretDownBold />}
          </span>
        )}
      </>
    )

    return (
      <ListItem
        key={item.path}
        className="relative flex flex-col px-2 mb-2"
        disablePadding
      >
        {item.children ? (
          <div
            onClick={() => toggleMenu(item.path)}
            className={`w-full relative cursor-pointer ${baseClasses} ${
              active ? activeClasses : inactiveClasses
            }`}
          >
            {itemContent}
          </div>
        ) : (
          <Link
            to={item.path}
            className={`w-full relative ${baseClasses} ${
              active ? activeClasses : inactiveClasses
            }`}
          >
            {itemContent}
          </Link>
        )}

        {item.children && isOpen && (
          <ul className="w-full py-1 bg-white z-10 list-none m-0 p-0">
            {item.children.map(child => (
              <li key={child.path} className="text-sm">
                <Link
                  to={child.path}
                  className={`block pl-12 pr-4 py-2 no-underline transition-colors duration-200 ease-in-out text-gray-600 hover:text-black hover:bg-gray-100 rounded-md ${
                    child.path === location.pathname
                      ? 'text-primary'
                      : 'text-gray-600'
                  }`}
                >
                  {child.label}
                </Link>
              </li>
            ))}
          </ul>
        )}
      </ListItem>
    )
  }

  return (
    <Box className="fixed top-0 left-0 w-[250px] h-full bg-white shadow-md transition-transform duration-300 ease-in-out z-999 overflow-y-auto flex flex-col scrollbar-hide">
      <div className="p-2 border-b border-gray-200 flex">
        <img
          src="/assets/images/swapspace-new-logo3.png"
          alt="Swap Space Admin"
          className="h-auto"
          width="80px"
        />
      </div>

      <nav className="flex-1 overflow-y-auto p-2 scrollbar-hide">
        <Typography
          variant="h3"
          className="py-2 px-2 text-sm font-semibold text-gray-400 uppercase tracking-wide"
        >
          General
        </Typography>
        <List className="list-none m-0 p-0">
          {menuItems.map(item => renderMenuItem(item))}
        </List>
        <div className="mt-8">
          <Typography
            variant="h3"
            className="py-2 px-2 text-sm font-semibold text-gray-400 uppercase tracking-wide"
          >
            Support
          </Typography>
          <List className="list-none m-0 p-0">
            {footerItems.map(item => renderMenuItem(item))}
          </List>
        </div>
      </nav>
    </Box>
  )
}

export default Sidebar